// src/components/LibraryShelves.jsx
//
// Library "bookshelf" presentation layer. ADDITIVE top layer over the pack library:
// each exam board (pack.paper) is one shelf, one book per pack. Clicking a shelf opens
// it (accordion, one at a time) and the board's EXISTING pack cards render below via
// the renderCard prop -- the SAME renderer DashboardPage's grid uses, unchanged.
//
// Data-driven from state.packs (already sorted by displayOrder in usePacksBridge), so
// admin add/remove/reorder flows through automatically.
//
// library-2-shelf / library-2b-spines: the CONCEPT is unchanged; the look is Direction A.
// The COLLAPSED shelf shows standing SPINES again (a book per pack, vertical title,
// varied heights and a slight tilt) -- but flat and token-driven: no leather, grain,
// foil, plaque or serif. Opening a shelf still reveals the unchanged pack cards.
// Variant V1: completion is a translucent band rising from the foot of each spine.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getPackTheme, COVER_INK_LIGHT } from '../utils/packBadge.js';
import { IconLock, IconClock } from './icons.jsx';
import './libraryShelves.css';

// Per-spine height / width / tilt, cycled by index -- deterministic (stable across
// re-renders, no random), carried over from the original shelf so the row keeps its
// hand-shelved rhythm rather than looking like a bar chart.
const SPINE_HEIGHTS = [126, 134, 118, 128, 132, 122];
const SPINE_WIDTHS = [42, 38, 44, 40, 46, 41];
const SPINE_TILTS = [-1, 0, 1, 0, -1, 0];
const SPINE_MAX_W = 60;

// Vertical title fit. The title runs along the spine's HEIGHT (writing-mode:
// vertical-rl), so height -- not width -- decides how much fits. The ladder is
// 12px -> 11px -> widen -> ellipsis: widening cannot fit more characters on a single
// line, but a long-titled pack reading as a THICKER book is the old shelf's "thick
// tome" cue, so the ellipsis lands on a book that looks like it earned it.
export function spineFit(label, height, baseWidth) {
    const text = String(label == null ? '' : label);
    const avail = Math.max(0, height - 34);        // top code/glyph + bottom breathing
    // Average advance of Plus Jakarta Sans 700 as a fraction of the font size.
    const fits = (fs) => text.length * fs * 0.58 <= avail;
    if (fits(12)) return { fs: 12, width: baseWidth, ellipsis: false };
    if (fits(11)) return { fs: 11, width: baseWidth, ellipsis: false };
    return { fs: 11, width: Math.min(SPINE_MAX_W, baseWidth + 10), ellipsis: true };
}

// The short exam code inside a board name ("Edexcel IGCSE English Language A (4EA1)"
// -> "4EA1"), for the tiny label at the top of a spine. Returns null when the board
// name carries no code-shaped token, in which case the label is omitted entirely
// rather than crowding the spine with prose.
export function shortPaperCode(paper) {
    const s = String(paper == null ? '' : paper).trim();
    if (!s) return null;
    const bracketed = /\(([^)]{2,10})\)\s*$/.exec(s);
    if (bracketed && /[0-9]/.test(bracketed[1])) return bracketed[1].trim();
    const tokens = s.split(/[\s/·]+/).filter(Boolean);
    for (let i = tokens.length - 1; i >= 0; i--) {
        const t = tokens[i].replace(/[(),.]/g, '');
        // A code has digits and is short: 4EA1, 9275, P1Q4.
        if (t.length >= 2 && t.length <= 6 && /[0-9]/.test(t) && /^[A-Za-z0-9]+$/.test(t)) return t.toUpperCase();
    }
    return null;
}

// (library-2c: the local coverInk() is gone. The ink now comes from the pack's derived
// theme -- getPackTheme(p).primary -- so the spine, the storefront cover card and the
// admin preview cannot drift apart on what "readable on this colour" means.)

// Group packs by exam board, preserving the catalog's displayOrder both across shelves
// (first-seen board order) and within a shelf. UNCHANGED from the previous version.
export function groupByBoard(packs) {
    const map = new Map();
    (packs || []).forEach((p) => {
        const board = p.paper || 'Study Packs';
        if (!map.has(board)) map.set(board, []);
        map.get(board).push(p);
    });
    return [...map.entries()]; // [ [board, packs[]], ... ]
}

// V1 completion: a translucent band rising from the FOOT of the spine to the pack's
// percentage, with a stronger top edge so the level reads as a line. 0% renders
// nothing -- an untouched pack is just a book.
//
// library-2c changed BOTH colours, and the reason matters. The band lies under the
// vertical title, so whatever it does to the cover it does under live text:
//   - the FILL washes AWAY from the ink (a dark cover gets darker, a light cover
//     lighter). 2b washed TOWARD it, which only survived because the dark-academia
//     covers were near-black; on the brighter 2c palette that dropped 7 of the 10
//     below 4.5:1. Washing away instead lifts every swatch to 6.4:1 or better.
//   - the EDGE is the pack's own ink at 55%, so the level line inherits the ink's
//     contrast guarantee instead of needing its own per-colour tuning.
function SpineFill({ percent, darkInk }) {
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    if (pct <= 0) return null;
    return (
        <span className="btl-spine-fill" aria-hidden="true"
            style={{
                height: pct + '%',
                background: darkInk ? 'rgba(255,255,255,.18)' : 'rgba(0,0,0,.18)',
                borderTopColor: darkInk ? 'rgba(27,26,23,.55)' : 'rgba(255,255,255,.55)',
            }} />
    );
}

export default function LibraryShelves({ packs, renderCard, completionFor }) {
    const shelves = useMemo(() => groupByBoard(packs), [packs]);

    // Accordion: one shelf open at a time; ALL CLOSED by default.
    const [openBoard, setOpenBoard] = useState(null);

    // Live column count of the tile grid, measured from the grid's OWN computed
    // template, so the reveal panel can be slotted after the row holding the open tile.
    const gridRef = useRef(null);
    const [cols, setCols] = useState(1);
    useEffect(() => {
        const el = gridRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const update = () => {
            const t = getComputedStyle(el).gridTemplateColumns;
            setCols(t && t !== 'none' ? t.split(' ').length : 1);
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    if (shelves.length === 0) return null;

    const toggle = (board) => {
        const willOpen = openBoard !== board;
        setOpenBoard(willOpen ? board : null);
        if (willOpen) {
            const smooth = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
            setTimeout(() => {
                const el = document.querySelector('.btl-shelf-reveal');
                if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
            }, 200);
        }
    };

    const openIdx = shelves.findIndex(([b]) => b === openBoard);
    const openEntry = openIdx >= 0 ? shelves[openIdx] : null;

    // The reveal panel is a full-span grid item spliced in DOM order right after the
    // LAST TILE OF THE OPEN TILE'S ROW, so the cards drop in under what was clicked.
    const insertAfter = openEntry
        ? Math.min(shelves.length - 1, (Math.floor(openIdx / Math.max(1, cols)) + 1) * Math.max(1, cols) - 1)
        : -1;

    const items = shelves.map(([board, group]) => {
        const open = openBoard === board;
        return (
            <section key={board} className={'btl-shelf' + (open ? ' btl-shelf--open' : '')}>
                <button type="button" className="btl-shelf-unit" aria-expanded={open}
                    onClick={() => toggle(board)}>
                    <div className="btl-shelf-case">
                        <div className="btl-shelf-books">
                            {group.map((p, i) => {
                                // Admin-set short spine name; blank falls back to the full label.
                                const name = p.spineLabel || p.label;
                                // getPackTheme already resolves coverColor > theme key >
                                // default, and derives the ink -- nothing to decide here.
                                const theme = getPackTheme(p);
                                const cover = theme.cover;
                                const ink = theme.primary;
                                const darkInk = ink !== COVER_INK_LIGHT;   // a light cover
                                const comingSoon = p.status === 'coming-soon';
                                const locked = p.locked === true;
                                const height = SPINE_HEIGHTS[i % SPINE_HEIGHTS.length];
                                const tilt = SPINE_TILTS[i % SPINE_TILTS.length];
                                const fit = spineFit(name, height, SPINE_WIDTHS[i % SPINE_WIDTHS.length]);
                                const code = shortPaperCode(p.paper);
                                // completionFor is supplied by the dashboard; null -> no band.
                                const done = typeof completionFor === 'function' ? completionFor(p) : null;
                                return (
                                    <div key={p.id}
                                        className={'btl-spine' + (comingSoon || locked ? ' btl-spine--dim' : '')}
                                        title={name}
                                        style={{
                                            background: cover,
                                            color: ink,
                                            height: height + 'px',
                                            width: fit.width + 'px',
                                            '--btl-tilt': tilt + 'deg',
                                        }}>
                                        {!comingSoon && <SpineFill percent={done === null ? 0 : done.percent} darkInk={darkInk} />}
                                        <span className="btl-spine-top">
                                            {comingSoon
                                                ? <IconClock className="btl-spine-glyph" />
                                                : locked
                                                    ? <IconLock className="btl-spine-glyph" />
                                                    : (code ? <span className="btl-spine-code">{code}</span> : null)}
                                        </span>
                                        <span className={'btl-spine-title' + (fit.ellipsis ? ' btl-spine-title--clip' : '')}
                                            style={{ fontSize: fit.fs + 'px' }}>
                                            {name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="btl-shelf-board" />
                    </div>
                    <div className="btl-shelf-caption">
                        <h3>{board}</h3>
                        <div className="btl-shelf-packsin">{group.map((p) => p.label).join(' · ')}</div>
                        <span className="btl-shelf-opencue">{open ? 'Close collection' : 'Open collection'}</span>
                    </div>
                </button>
            </section>
        );
    });

    if (openEntry) {
        items.splice(insertAfter + 1, 0, (
            <div className="btl-shelf-reveal" key={'reveal-' + openEntry[0]}>
                <div className="btl-shelf-reveal-head"><h3>{openEntry[0]}</h3></div>
                {/* Same grid classes as the DashboardPage library grid, so the revealed
                    cards render exactly as they do today. */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {openEntry[1].map((p, idx) => (
                        <div key={p.id} className="btl-shelf-pwrap"
                            style={{ animationDelay: (idx * 0.04) + 's' }}>
                            {renderCard(p, idx)}
                        </div>
                    ))}
                </div>
            </div>
        ));
    }

    return (
        <div>
            <p className="btl-shelf-hint">Tap a shelf to open the collection</p>
            <div className="btl-shelf-grid" ref={gridRef}>
                {items}
            </div>
        </div>
    );
}
